import Alert from '../../components/ui/Alert';
import { testId } from '../../utils';
import { Iconography } from './Iconography';
import { Typography } from './Typography';
import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';
import React from 'react';

interface ModalProps {
  triggerElement?: React.ReactNode;
  title: React.ReactNode;
  contentElement: React.ReactNode;
  closeElement?: React.ReactNode;
  icon?: React.ReactNode;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  'data-testid': string;
  submitElement?: React.ReactNode;
  serverError?: string | React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({
  triggerElement,
  title,
  contentElement,
  closeElement,
  icon,
  open,
  'data-testid': dataTestId,
  submitElement,
  setOpen,
  serverError,
}) => {
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      {triggerElement && (
        <Dialog.Trigger {...testId(dataTestId, 'trigger-wrapper')} asChild>
          {triggerElement}
        </Dialog.Trigger>
      )}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 data-[state=open]:animate-overlayShow bg-black bg-opacity-50" />
        <Dialog.Content
          {...testId(dataTestId)}
          aria-describedby={undefined}
          className={clsx(
            'flex flex-col gap-6',
            'fixed left-1/2 top-16 sm:top-1/3 max-h-[85vh] w-[90vw] max-w-[448px] p-6 md:p-8',
            '-translate-x-1/2 sm:-translate-y-1/2 -translate-y-[0px] rounded-md',
            'bg-white focus:outline-none data-[state=open] :animate-contentShow',
          )}
        >
          <Dialog.Close asChild>
            <div className="flex justify-end">
              <button
                {...testId(dataTestId, 'close-x')}
                className="text-black-dark"
                aria-label="Close"
              >
                <Iconography variant="close" style="rounded" />
              </button>
            </div>
          </Dialog.Close>
          {icon && <div className="flex justify-center">{icon}</div>}
          <Dialog.Title
            {...testId(dataTestId, 'title')}
            className="text-center"
          >
            <Typography variant="title-default">{title}</Typography>
          </Dialog.Title>
          {!!serverError && (
            <Alert variant="error" {...testId(dataTestId, 'error-message')}>
              {serverError}
            </Alert>
          )}

          <fieldset
            {...testId(dataTestId, 'content')}
            className="flex flex-col gap-y-6"
          >
            {contentElement}
          </fieldset>
          {closeElement && (
            <div className="flex gap-2 flex-col md:flex-row">
              {submitElement}
              <Dialog.Close asChild>{closeElement}</Dialog.Close>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
